import { styled } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import { forwardRef, useEffect, useRef, useState } from 'react';
import CheckIcon from '@/assets/check.svg?react';
import SearchIcon from '@/assets/search.svg?react';
import { useClickAway } from '@/hooks/useClickAway';
import { FormattedMessage } from 'react-intl';
import Popper from '@/components/Popper/Popper';
import { isMultiple, SelectInterface } from './SelectHelpers';
import Checkbox from '../Checkbox/Checkbox';
import Button from '../Button/Button';
import SelectHead from './SelectHead';

const Select = forwardRef(
  <T extends string | number | null>(
    props: SelectInterface<T>,
    popperRef: React.Ref<HTMLDivElement>,
  ) => {
    const {
      value,
      placeholder,
      options,
      searchEnabled = false,
      searchPlaceholder = 'Search',
      onSearchChange,
      disableLocalFiltering = false,
      disableSelectAll = false,
      selectedOptions: selectedOptionsProp,
      variant = 'outlined',
      size = 'medium',
      disabled = false,
      'aria-label': ariaLabel,
      onDropdownToggle,
      showClear: showClearProp,
      listRef: listRefProp,
      listWidth,
      loading = false,
      loadingNextPage = false,
    } = props;
    const [searchText, setSearchText] = useState('');
    const [open, setOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);
    const internalListRef = useRef<HTMLDivElement>(null);
    const listRef = listRefProp || internalListRef;
    const multiple = isMultiple(props);
    const isOptionSelected = (option: (typeof options)[number]) =>
      multiple
        ? (value as T[])?.includes?.(option.value)
        : option.value === value;

    const filteredOptions = disableLocalFiltering
      ? options
      : options.filter((option) => {
          return (
            searchText === '' ||
            (option.text || option.value)
              ?.toString()
              .toLowerCase()
              .includes(searchText.toLowerCase())
          );
        });

    const visibleOptions = [...filteredOptions].sort((a, b) => {
      const aSelected = isOptionSelected(a);
      const bSelected = isOptionSelected(b);
      if (aSelected === bSelected) return 0;
      return aSelected ? -1 : 1;
    });
    const [width, setWidth] = useState(0);
    const noSearchResults =
      visibleOptions.length === 0 && options.length > 0;
    const selectedOptions =
      selectedOptionsProp ??
      options.filter((option) => isOptionSelected(option));

    const lockedValues = options
      .filter((option) => option.disabled)
      .map((option) => option.value) as T[];

    useClickAway(rootRef, (e: MouseEvent) => {
      if (listRef.current?.contains(e.target as Node)) return;

      setSearchText('');
      onSearchChange?.('');
      setOpen(false);
    });

    useEffect(() => {
      if (rootRef?.current) {
        setWidth(rootRef?.current?.scrollWidth);
      }
    }, []);

    useEffect(() => {
      onDropdownToggle?.(open);
    }, [open, onDropdownToggle]);

    const handleOptionClick = (clickValue: T) => {
      const clickedOption = options.find(
        (option) => option.value === clickValue,
      );
      if (clickedOption?.disabled) return;

      if (multiple) {
        const current = props.value as T[];
        const exists = current.includes(clickValue);
        if (exists) {
          props.onChange(current.filter((v) => v !== clickValue));
        } else {
          props.onChange([...current, clickValue]);
        }
      } else {
        if (value === clickValue) {
          props.onChange(null);
        } else {
          props.onChange(clickValue);
        }
        setOpen(false);
        setSearchText('');
        onSearchChange?.('');
      }
    };

    const handleSelectAllClick = () => {
      if (!isMultiple(props)) return;

      props.onChange(options.map((option) => option.value) as T[]);
      setOpen(false);
      setSearchText('');
      onSearchChange?.('');
    };

    const handleClearClick = () => {
      if (!isMultiple(props)) return;

      props.onChange(lockedValues);
      setOpen(false);
      setSearchText('');
      onSearchChange?.('');
    };

    const handleHeadClearClick = () => {
      if (multiple) {
        props.onChange(lockedValues);
      } else if (value === null || value === undefined || !lockedValues.includes(value as T)) {
        props.onChange(null);
      }
      setOpen(false);
      setSearchText('');
      onSearchChange?.('');
    };

    const handleSearchOnChange = (
      e: React.ChangeEvent<HTMLInputElement>,
    ) => {
      const newSearchText = e.target.value;
      setSearchText(newSearchText);
      onSearchChange?.(newSearchText);
    };

    return (
      <Root ref={rootRef} variant={variant} disabled={disabled}>
        <SelectHead
          open={open}
          variant={variant}
          onClick={() => {
            if (!disabled) {
              setOpen((o) => !o);
            }
          }}
          multiple={multiple}
          selectedOptions={selectedOptions}
          placeholder={placeholder}
          disabled={disabled}
          showClear={showClearProp ?? selectedOptions.length > 0}
          onClear={handleHeadClearClick}
          aria-label={ariaLabel}
        />
        <StyledPopper
          ref={popperRef}
          open={open && !disabled}
          anchorEl={rootRef.current}
          onClose={() => setOpen(false)}
          aria-label="Select options popper"
        >
          <List
            variant={variant}
            style={{ width: Math.max(width, listWidth ?? 0) }}
            ref={listRef}
            role="listbox"
          >
            <ListContent variant={variant} size={size}>
              {searchEnabled && (
                <SearchWrapper>
                  <SearchTextField
                    placeholder={searchPlaceholder}
                    value={searchText}
                    onChange={handleSearchOnChange}
                    $size={size}
                    aria-label="Search options"
                  />
                  <StyledSearchIcon size={size} />
                </SearchWrapper>
              )}
              {loading ? (
                <LoaderWrapper
                  role="status"
                  aria-label="Loading options"
                >
                  <CircularProgress />
                </LoaderWrapper>
              ) : (
                <>
                  {multiple ? (
                    <Row
                      variant={variant}
                      size={size}
                      justifyEnd={disableSelectAll}
                    >
                      {!disableSelectAll && (
                        <Button
                          variant="text"
                          customVariant="text-small"
                          onClick={handleSelectAllClick}
                          aria-label="Select all options"
                        >
                          <FormattedMessage
                            id="selectAll"
                            defaultMessage="Select All"
                          ></FormattedMessage>
                        </Button>
                      )}
                      <Button
                        variant="text"
                        customVariant="text-small"
                        onClick={handleClearClick}
                        aria-label="Clear all selections"
                      >
                        <FormattedMessage
                          id="clear"
                          defaultMessage="Clear"
                        ></FormattedMessage>
                      </Button>
                    </Row>
                  ) : null}
                  {options.length === 0 && (
                    <Row variant={variant} size={size} role="option">
                      <RowLabel info>
                        <FormattedMessage
                          id="noOptions"
                          defaultMessage="No options available"
                        />
                      </RowLabel>
                    </Row>
                  )}
                  {noSearchResults && (
                    <Row variant={variant} size={size} role="option">
                      <RowLabel info>
                        <FormattedMessage
                          id="noSearchResults"
                          defaultMessage="No search results"
                        />
                      </RowLabel>
                    </Row>
                  )}
                  {visibleOptions.map((option) => (
                    <Row
                      key={option.id}
                      active
                      selected={isOptionSelected(option)}
                      optionDisabled={option.disabled}
                      onClick={() => handleOptionClick(option.value)}
                      variant={variant}
                      size={size}
                      role="option"
                      aria-selected={isOptionSelected(option)}
                      aria-disabled={option.disabled}
                      aria-label={`${
                        typeof option.label === 'string'
                          ? option.label
                          : option.value
                      } option`}
                    >
                      {multiple ? (
                        <Checkbox
                          checked={isOptionSelected(option)}
                          disabled={option.disabled}
                        />
                      ) : null}
                      {option.icon ? (
                        <RowIcon size={size}>{option.icon}</RowIcon>
                      ) : null}
                      <RowLabel>{option.label}</RowLabel>
                      {variant === 'text' && isOptionSelected(option) ? (
                        <CheckIcon />
                      ) : null}
                    </Row>
                  ))}
                  {loadingNextPage && (
                    <Row variant={variant} size={size} role="option">
                      <RowLabel info>
                        <FormattedMessage
                          id="loadingMore"
                          defaultMessage="Loading more..."
                        />
                      </RowLabel>
                    </Row>
                  )}
                </>
              )}
            </ListContent>
          </List>
        </StyledPopper>
      </Root>
    );
  },
);

Select.displayName = 'Select';

const Root = styled('div')<{
  variant: 'text' | 'outlined';
  disabled?: boolean;
}>(({ theme, variant, disabled = false }) => ({
  position: 'relative',
  width: '100%',
  minWidth: variant === 'outlined' ? '350px' : '120px',
  height: '48px',
  border:
    variant === 'outlined'
      ? `1px solid ${theme.palette.grey200}`
      : 'none',
  background:
    variant === 'outlined' ? theme.palette.white : 'transparent',
  borderRadius: theme.radii.xs,
  color: disabled
    ? theme.palette.text?.disabled || '#00000042'
    : theme.palette.textPrimaryBlack,
  opacity: disabled ? 0.6 : 1,
  pointerEvents: disabled ? 'none' : 'auto',
  transition: 'all 0.2s ease-in-out',
}));

const StyledPopper = styled(Popper)(() => ({
  zIndex: 99999,
  maxHeight: '300px',
  overflowY: 'auto',
}));

const List = styled('div')<{ variant: 'text' | 'outlined' }>(
  ({ theme, variant }) => ({
    width: '100%',
    overflow: 'hidden',
    transition: 'max-height 0.2s ease-in-out',
    zIndex: 1,
    borderRadius: theme.radii.xs,
    boxShadow:
      '0px 10px 32px -4px #1319271A, 0px 6px 14px -6px #1319271F',

    '> div > div:not(:first-of-type)': {
      borderTop:
        variant === 'outlined'
          ? `1px solid ${theme.palette.grey200}`
          : 'none',
    },
  }),
);

const ListContent = styled('div')<{
  variant: 'text' | 'outlined';
  size: 'small' | 'medium';
}>(({ theme, variant, size }) => {
  let padding = '0';

  if (variant === 'outlined' && size === 'medium') {
    padding = `${theme.spacing(1)} 0`;
  }

  return {
    width: '100%',
    padding,
    background: theme.palette.white,
    borderRadius: theme.radii.xs,
    border: `1px solid ${theme.palette.grey200}`,
  };
});

const SearchWrapper = styled('div')({
  position: 'relative',
  width: '100%',
});

const SearchTextField = styled('input')<{
  $size: 'small' | 'medium';
}>(({ $size }) => ({
  boxSizing: 'border-box',
  width: '100%',
  height: '40px',
  padding:
    $size === 'small' ? '10px 24px 10px 40px' : '10px 24px 10px 54px',
  border: 'none',
  outline: 'none',
}));

const StyledSearchIcon = styled(SearchIcon)<{
  size: 'small' | 'medium';
}>(({ size }) => ({
  position: 'absolute',
  top: '50%',
  left: size === 'small' ? '11px' : '24px',
  transform: 'translateY(-50%)',
  width: '18px',
  height: '18px',
  pointerEvents: 'none',
}));

const Row = styled('div')<{
  active?: boolean;
  selected?: boolean;
  optionDisabled?: boolean;
  variant: 'text' | 'outlined';
  size: 'small' | 'medium';
  justifyEnd?: boolean;
}>(({
  active = false,
  selected,
  optionDisabled = false,
  theme,
  variant,
  size,
  justifyEnd = false,
}) => {
  let height = 40;
  let padding = '6px 12px';
  if (variant === 'outlined' && size === 'medium') {
    height = 48;
    padding = '14px';
  } else if (variant === 'outlined' && size === 'small') {
    height = 38;
    padding = '6px 8px';
  }

  return {
    height,
    display: 'flex',
    alignItems: 'center',
    justifyContent: justifyEnd ? 'flex-end' : 'space-between',
    padding,
    cursor: optionDisabled ? 'not-allowed' : active ? 'pointer' : 'default',
    opacity: optionDisabled ? 0.6 : 1,
    userSelect: 'none',
    background:
      selected && variant === 'outlined'
        ? theme.palette.grey100
        : 'transparent',
    transition: '0.2s all ease-in-out',
  };
});

const RowIcon = styled('div')<{ size: 'small' | 'medium' }>(
  ({ theme, size }) => ({
    width: size === 'small' ? 16 : 30,
    height: size === 'small' ? 16 : 30,
    marginRight:
      size === 'small' ? theme.spacing(1) : theme.spacing(2),
  }),
);

const RowLabel = styled('div')<{ info?: boolean }>(
  ({ theme, info = false }) => ({
    flexGrow: 1,
    textAlign: info ? 'center' : 'left',
    color: info
      ? theme.palette.grey600
      : theme.palette.textPrimaryBlack,
    fontSize: 14,
  }),
);

const LoaderWrapper = styled('div')({
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '20px',
});

export default Select;
